import { useState, useEffect } from "react";
import {
  RightPanel,
  RightPanelSection,
  RightPanelSectionTitle,
  RightPanelField,
  RightPanelGrid,
} from "@/components/RightPanel";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Plus,
  X,
  Image as ImageIcon,
  User as UserIcon,
} from "lucide-react";
import {
  Case,
  api,
  CaseStatistics,
  URL,
  CaseMetadataText,
  CaseMetadataImage,
} from "@/lib/api";
import { Input } from "@/components/ui/input";

export interface CaseDetailsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseData: Case | null;
}

/**
 * CaseDetailsPanel - Complete right panel for Case details
 *
 * Usage:
 * const [selectedCase, setSelectedCase] = useState<Case | null>(null);
 * const [isPanelOpen, setIsPanelOpen] = useState(false);
 *
 * <CaseDetailsPanel
 *   open={isPanelOpen}
 *   onOpenChange={setIsPanelOpen}
 *   caseData={selectedCase}
 * />
 */
export function CaseDetailsPanel({
  open,
  onOpenChange,
  caseData,
}: CaseDetailsPanelProps) {
  const [statistics, setStatistics] = useState<CaseStatistics | null>(null);
  const [urls, setUrls] = useState<URL[]>([]);
  const [showAllUrls, setShowAllUrls] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keywords, setKeywords] = useState<CaseMetadataText[]>([]);
  const [exactMatchImages, setExactMatchImages] = useState<CaseMetadataImage[]>(
    [],
  );
  const [facialImages, setFacialImages] = useState<CaseMetadataImage[]>([]);
  const [newKeyword, setNewKeyword] = useState("");

  // Fetch case statistics when panel opens
  useEffect(() => {
    if (!caseData || !open) {
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [statsData, urlsData, keywordsData, imageData] =
          await Promise.all([
            api.cases.getStatistics(caseData.id),
            api.cases.getUrls(caseData.id),
            api.cases.getTextMetadata(caseData.id).catch(() => []),
            api.cases.getImageMetadata(caseData.id).catch(() => []),
          ]);

        setStatistics(statsData);
        setUrls(urlsData);
        setKeywords(keywordsData);
        // Separate images by match_type
        setExactMatchImages(
          imageData.filter((img) => img.match_type === "EXACT_MATCH"),
        );
        setFacialImages(
          imageData.filter((img) => img.match_type === "RECOGNITION"),
        );
      } catch (err) {
        console.error("Error fetching case details:", err);
        setError("Failed to load case details");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [caseData, open]);

  if (!caseData) {
    return null;
  }

  const displayedUrls = showAllUrls ? urls : urls.slice(0, 5);

  return (
    <RightPanel open={open} onOpenChange={onOpenChange} title="Case Details">
      {loading && !statistics ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="text-sm text-destructive bg-destructive/10 p-4 rounded-lg">
          {error}
        </div>
      ) : (
        <>
          {/* Case Name */}
          <RightPanelSection>
            <div className="text-lg font-semibold text-foreground">
              {statistics?.case_info.name || caseData.name}
            </div>
            <div className="flex gap-2 mt-2">
              <StatusBadge
                status={statistics?.case_info.status || caseData.status}
                label={
                  statistics?.case_info.status?.replace("_", " ") ||
                  caseData.status
                }
              />
              <Badge variant="outline" className="text-xs">
                {statistics?.case_info.type || caseData.type}
              </Badge>
            </div>
          </RightPanelSection>

          <Separator />

          {/* Basic Info */}
          <RightPanelSection>
            <RightPanelGrid columns={2}>
              <RightPanelField
                label="Agency"
                value={
                  statistics?.agency.name ? (
                    <Badge
                      variant="outline"
                      className="bg-blue-50 text-blue-800 border-blue-200"
                    >
                      {statistics.agency.abbreviation || statistics.agency.name}
                    </Badge>
                  ) : (
                    "—"
                  )
                }
              />
              <RightPanelField
                label="Case Number"
                value={statistics?.case_info.case_number || "—"}
              />
            </RightPanelGrid>
            <RightPanelGrid columns={2}>
              <RightPanelField
                label="Created"
                value={
                  statistics?.case_info.created_at
                    ? new Date(statistics.case_info.created_at).toLocaleString(
                        "en-US",
                        {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        },
                      )
                    : "—"
                }
              />
              <RightPanelField
                label="Last Updated"
                value={
                  statistics?.case_info.updated_at
                    ? new Date(statistics.case_info.updated_at).toLocaleString(
                        "en-US",
                        {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        },
                      )
                    : "—"
                }
              />
            </RightPanelGrid>
          </RightPanelSection>

          <Separator />

          {/* Statistics */}
          <RightPanelSection>
            <RightPanelSectionTitle>Statistics</RightPanelSectionTitle>
            <RightPanelGrid columns={2}>
              <RightPanelField
                label="Total URLs"
                value={statistics?.urls.total_urls || 0}
              />
              <RightPanelField
                label="Total Views"
                value={statistics?.urls.total_views || 0}
              />
            </RightPanelGrid>
            {statistics?.urls.status_breakdown && (
              <div className="mt-3">
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                  URL Status Breakdown
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(statistics.urls.status_breakdown).map(
                    ([status, count]) => (
                      <div
                        key={status}
                        className="flex items-center gap-1 text-xs"
                      >
                        <StatusBadge status={status} />
                        <span className="text-muted-foreground">{count}</span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}
            <RightPanelGrid columns={2}>
              <RightPanelField
                label="Evidence Packages"
                value={statistics?.evidence.total_packages || 0}
              />
              <RightPanelField
                label="First Capture"
                value={
                  statistics?.evidence.first_capture
                    ? new Date(
                        statistics.evidence.first_capture,
                      ).toLocaleDateString()
                    : "—"
                }
              />
            </RightPanelGrid>
          </RightPanelSection>

          <Separator />

          {/* Assigned Users */}
          <RightPanelSection>
            <RightPanelSectionTitle>
              Assigned Users ({statistics?.assigned_users.length || 0})
            </RightPanelSectionTitle>
            <div className="space-y-2">
              {statistics?.assigned_users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-2 rounded-lg bg-muted/30"
                >
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {user.display_name || user.email}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {user.email}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {user.permission}
                  </Badge>
                </div>
              ))}
              {!statistics?.assigned_users.length && (
                <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
                  No users assigned yet.
                </div>
              )}
            </div>
          </RightPanelSection>

          <Separator />

          {/* URLs List */}
          <RightPanelSection>
            <div className="flex items-center justify-between">
              <RightPanelSectionTitle>
                URLs ({urls.length})
              </RightPanelSectionTitle>
              {urls.length > 5 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAllUrls(!showAllUrls)}
                >
                  {showAllUrls ? (
                    <>
                      <ChevronDown className="h-4 w-4 mr-1" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronRight className="h-4 w-4 mr-1" />
                      Show All
                    </>
                  )}
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {displayedUrls.map((url) => (
                <div
                  key={url.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => {
                    // TODO: Open URL details panel or navigate to URL
                    console.log("Open URL:", url.id);
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono text-foreground truncate">
                      {url.url}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <StatusBadge status={url.status} />
                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
              {urls.length === 0 && (
                <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
                  No URLs in this case yet.
                </div>
              )}
            </div>
          </RightPanelSection>

          <Separator />

          {/* Site Type Breakdown */}
          {statistics?.evidence.content_status_breakdown && (
            <>
              <RightPanelSection>
                <RightPanelSectionTitle>
                  Site Type Breakdown
                </RightPanelSectionTitle>
                <div className="space-y-2">
                  {Object.entries(
                    statistics.evidence.content_status_breakdown,
                  ).map(([type, count]) => (
                    <div
                      key={type}
                      className="flex items-center justify-between"
                    >
                      <span className="text-sm text-foreground capitalize">
                        {type}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {count} (
                        {Math.round(
                          (count / statistics.evidence.total_packages) * 100,
                        )}
                        %)
                      </Badge>
                    </div>
                  ))}
                </div>
              </RightPanelSection>
              <Separator />
            </>
          )}

          {/* Search and Monitoring */}
          <RightPanelSection>
            <RightPanelSectionTitle>
              Search and Monitoring
            </RightPanelSectionTitle>

            {/* Keywords */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-foreground">KEYWORD</div>
              <div className="text-xs text-muted-foreground">
                Add keywords to improve our ability to automatically locate more
                URLs
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add keyword..."
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  className="text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newKeyword.trim()) {
                      api.cases
                        .addTextMetadata(caseData.id, newKeyword, "PRUNE")
                        .then((metadata) => {
                          setKeywords([...keywords, metadata]);
                          setNewKeyword("");
                        })
                        .catch((err) =>
                          console.error("Failed to add keyword:", err),
                        );
                    }
                  }}
                />
                <Button
                  size="sm"
                  onClick={() => {
                    if (newKeyword.trim()) {
                      api.cases
                        .addTextMetadata(caseData.id, newKeyword, "PRUNE")
                        .then((metadata) => {
                          setKeywords([...keywords, metadata]);
                          setNewKeyword("");
                        })
                        .catch((err) =>
                          console.error("Failed to add keyword:", err),
                        );
                    }
                  }}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {keywords.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {keywords.map((keyword) => (
                    <Badge
                      key={keyword.id}
                      variant="outline"
                      className="pl-2 pr-1 py-1 gap-1"
                    >
                      <span className="text-xs">{keyword.contents}</span>
                      <button
                        onClick={() => {
                          api.cases
                            .deleteTextMetadata(caseData.id, keyword.id)
                            .then(() => {
                              setKeywords(
                                keywords.filter((k) => k.id !== keyword.id),
                              );
                            })
                            .catch((err) =>
                              console.error("Failed to delete keyword:", err),
                            );
                        }}
                        className="hover:bg-destructive/20 rounded-sm p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Image Search - Exact Match */}
            <div className="mt-4 space-y-2">
              <div className="text-xs font-medium text-foreground">
                IMAGE SEARCH - EXACT MATCH
              </div>
              <div className="text-xs text-muted-foreground">
                Upload images for exact matching
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full h-24 border-dashed"
              >
                <div className="flex flex-col items-center gap-1">
                  <Plus className="h-6 w-6" />
                  <span className="text-xs">Upload Image</span>
                </div>
              </Button>
              {exactMatchImages.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {exactMatchImages.map((img) => (
                    <div
                      key={img.id}
                      className="relative aspect-square bg-muted rounded-lg flex items-center justify-center border"
                    >
                      <ImageIcon className="h-6 w-6 text-muted-foreground" />
                      <button
                        className="absolute top-1 right-1 bg-background rounded-full p-1 hover:bg-destructive/20"
                        onClick={() => {
                          console.log("Delete image:", img.id);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Facial Recognition */}
            <div className="mt-4 space-y-2">
              <div className="text-xs font-medium text-foreground">
                FACIAL RECOGNITION
              </div>
              <div className="text-xs text-muted-foreground">
                Upload faces for facial recognition tracking
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full h-24 border-dashed"
              >
                <div className="flex flex-col items-center gap-1">
                  <Plus className="h-6 w-6" />
                  <span className="text-xs">Upload Face</span>
                </div>
              </Button>
              {facialImages.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {facialImages.map((img) => (
                    <div
                      key={img.id}
                      className="relative aspect-square bg-muted rounded-lg flex items-center justify-center border"
                    >
                      <UserIcon className="h-6 w-6 text-muted-foreground" />
                      <button
                        className="absolute top-1 right-1 bg-background rounded-full p-1 hover:bg-destructive/20"
                        onClick={() => {
                          console.log("Delete face:", img.id);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </RightPanelSection>

          <Separator />

          {/* Actions */}
          <RightPanelSection>
            <RightPanelSectionTitle>Actions</RightPanelSectionTitle>
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                size="sm"
              >
                Export Case Data
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                size="sm"
              >
                View Full Report
              </Button>
            </div>
          </RightPanelSection>
        </>
      )}
    </RightPanel>
  );
}
